import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { SphereAtmosphere, SphereDefs, SphereGlass } from './Sphere';
import PlanetSurface, { HAT_SURFACE, HAT_TILT, SurfaceDefs } from './PlanetSurface';
import { HatGlyph } from './HatIcon';

/*
 * Nine hats as a three-dimensional orrery.
 *
 * The orbits are circles viewed from above at a shallow angle, and no two lie
 * in the same plane: each is tilted by its culture's own inclination about a
 * line of nodes no other orbit shares. That is what makes nine rings read as a
 * system rather than as a target -- coplanar, they were concentric ellipses.
 *
 * The projection gives a real depth axis, and depth drives three things at
 * once --
 *
 *   - scale: near worlds are drawn larger, far ones smaller
 *   - order: bodies are sorted back-to-front every frame, so a planet on the
 *            far side passes behind the star and behind nearer planets
 *   - air:   distant worlds fade slightly, the way atmosphere does it
 *
 * With genuine occlusion, two planets overlapping is no longer a defect to be
 * engineered away -- it reads as one passing behind the other.
 *
 * Names appear only on hover or focus, so at most one label is ever on screen
 * and label collision remains impossible by construction. Each world carries
 * its hat's mark permanently; the count it used to show is in the label, in
 * the aria-label, and in the size of the world itself.
 *
 * Positions are driven by requestAnimationFrame writing transforms directly to
 * refs, rather than through React state, so the 60fps path never re-renders.
 */

/*
 * The frame is wider than the artwork needs because the orbits are not: at 30
 * units apart, two neighbouring worlds of maximum size had their limbs a
 * pixel from touching whenever they lined up radially. The spacing now clears
 * the largest possible pair by a comfortable margin, and the viewBox grew to
 * hold it. The svg scales to its container, so this costs nothing on screen.
 */
const W = 880;
const H = 490;
const CX = W / 2;
const CY = H / 2;
const A0 = 74; // innermost orbital radius
const STEP = 40; // spacing between orbits: 40 against a 26-unit largest pair
/*
 * How flat the disc reads. Lower is more edge-on, and more edge-on is what
 * makes an inclined orbit legible: at 0.5 the rings were near enough circular
 * that a tilted one just looked like a circle drawn slightly wrong.
 */
const COS_I = 0.34;

/*
 * Each orbit gets its own plane.
 *
 * `inclination` has been in the canon since the cultures were written and was
 * never drawn -- every world ran in the same flat ring, which is why nine
 * orbits read as one target rather than as a system. The tilt is about a line
 * of nodes, and the nodes are spread by the golden angle so no two orbits
 * share a hinge and the crossings never line up.
 */
export const NODE_SPREAD = 137.5;
export const RAD = Math.PI / 180;

/*
 * A point on an inclined orbit, and where it lands on screen.
 *
 * The flat orbit lies in the x-z plane; tilting it about a node line that also
 * lies in that plane throws the world up out of the disc, which is the whole
 * point. Rodrigues' rotation, with the cross product collapsing to the y axis
 * because both vectors are in the plane.
 */
export const orbitPoint = (a, theta, inc, node) => {
  const px = a * Math.cos(theta);
  const pz = a * Math.sin(theta);
  const nx = Math.cos(node);
  const nz = Math.sin(node);
  const ci = Math.cos(inc);
  const si = Math.sin(inc);
  const dot = nx * px + nz * pz;
  return {
    x: px * ci + nx * dot * (1 - ci),
    y: (nz * px - nx * pz) * si, // out of the disc
    z: pz * ci + nz * dot * (1 - ci), // toward the viewer
  };
};

/* Depth is foreshortened; height out of the disc is not. */
const screenX = (point) => CX + point.x;
const screenY = (point) => CY + point.z * COS_I - point.y;

/*
 * Where an orbit crosses the plane of the sky: the angle at which a world
 * stops receding and starts coming toward the viewer.
 *
 * On a flat orbit this is theta = 0 and the near half is simply the first half
 * turn. Tilted, it is not -- and splitting at 0 regardless drew the bright
 * near arc over part of the far side, which is why the highlighted track
 * appeared to miss the world riding it.
 *
 * Depth reduces to z = a(A cos t + B sin t), so the crossing is where that
 * vanishes, and z rises through it.
 */
export const ascendingNode = (inc, node) => {
  const nx = Math.cos(node);
  const nz = Math.sin(node);
  const ci = Math.cos(inc);
  return Math.atan2(-(nx * nz * (1 - ci)), ci + nz * nz * (1 - ci));
};
const MAX_PLANET = 13;
const MIN_PLANET = 7;
const DEPTH = 0.32; // how much nearer bodies grow
const STAR_R = 38; // Luca, at the centre of it all
/*
 * One period, shared by all nine.
 *
 * This is the one place the orrery stops being astronomy, and it is deliberate.
 * Keplerian periods mean every world turns at a different rate, and worlds at
 * different rates lap each other: measured over an hour, a Keplerian system
 * spends about a fifth of its time with seven or more of the nine bunched on
 * one side of the star, and the gap between two of them reaches zero.
 *
 * A spreading force does not rescue it either. The solver springs each world
 * back toward its true phase, and it is the true phases that clump -- pushed
 * hard enough to fix the picture, worlds bend 150 degrees off their own
 * position and it still clumps a quarter of the time.
 *
 * Equal rates hold the stagger exactly: the nine stay 40 degrees apart for as
 * long as the page is open. What is given up is that outer worlds now sweep
 * faster on screen than inner ones rather than slower. Radius, inclination,
 * node and size still differ, so the system reads as nine separate bodies --
 * it is only the phase relationship that is fixed.
 */
export const BASE_PERIOD = 64; // seconds for one turn, every orbit

/*
 * Keeping the worlds apart.
 *
 * Nine bodies on Keplerian periods conjunct sooner or later -- that is what
 * orbits do -- and a conjunction here means two marks drawn on top of each
 * other, which is the one thing the marks cannot survive. Depth sorting alone
 * does not rescue it: a 22% scale spread and a little haze are not strong
 * enough cues to make five bodies in one quadrant read as depth rather than as
 * a pile-up.
 *
 * So each world carries a phase offset that is pushed by its neighbours and
 * pulled back toward zero by a spring. Crowding bends a world forward or back
 * ALONG ITS OWN ORBIT -- theta only, so nothing ever leaves its ellipse -- and
 * the offset decays as soon as the pressure is off.
 *
 * The offset is deliberately carried between frames rather than resolved from
 * scratch each time. Solving it per frame is bistable: a hair's change in the
 * true angle flips which side of a conjunction the solver prefers, and the
 * whole system snaps. Measured over twenty simulated minutes, that version
 * teleported bodies up to 183px in a single frame. Integrating instead bounds
 * the rate of change by construction; the same run peaks at 1.6px.
 *
 * When two worlds stack radially -- one directly above the other at the top of
 * the disc -- neither tangent points at the other, so the gradient vanishes and
 * the pair would sit locked together. LEVER detects that stall and lets the
 * inner, faster world be the one that yields.
 */
const CLEAR = 9; // clear space demanded between two limbs
const PUSH = 3; // how hard crowding bends a world off its true phase
const SETTLE = 0.9; // how fast it drifts back once the pressure is off
const MAX_NUDGE = 0.8; // radians a world may ever be bent, about 46 degrees
const LEVER = 0.3; // below this tangential leverage, the inner world yields
/*
 * Each world's glass shell stands slightly proud of its core, so the shell is
 * what actually touches a neighbour. The solver measures the same edge the eye
 * does -- one constant, used by both, so they cannot drift apart.
 */
const SHELL = 1.03;
/*
 * How much of a world its mark takes up. The glyph box is 24 units and the
 * shapes reach about +/- 11 of it, so this multiple of the radius lands the
 * mark at a bit under half the diameter -- a marking ON a planet, with the lit
 * crown and the shaded limb still visible around it. At 1.75 it covered four
 * fifths of the face and every world read as a badge instead of a body.
 */
const GLYPH = 1;
const MAX_STEP = 1 / 30; // ignore the huge dt a backgrounded tab reports

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

/** Half of an orbit: the far side sits above the centre line, the near side below. */
const halfOrbit = (a, near, inc, node) => {
  /*
   * Sampled rather than solved. The projected shape of an inclined orbit is
   * still an ellipse, but finding its axes is fiddly and the result has to
   * match the worlds exactly -- running the same transform guarantees it.
   */
  const steps = 42;
  // Split where the orbit actually crosses the sky plane, not at theta = 0.
  const crossing = ascendingNode(inc, node);
  const from = near ? crossing : crossing + Math.PI;
  let d = '';
  for (let i = 0; i <= steps; i += 1) {
    const point = orbitPoint(a, from + (Math.PI * i) / steps, inc, node);
    d += `${i ? ' L' : 'M'} ${screenX(point).toFixed(2)} ${screenY(point).toFixed(2)}`;
  }
  return d;
};

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event) => setReduced(event.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
};

const HatOrrery = ({ hats, activeHat, onToggle, onClear, interactive = true }) => {
  const uid = useId().replace(/:/g, '');
  const reduced = usePrefersReducedMotion();
  const max = Math.max(...hats.map((hat) => hat.count), 1);
  const bodyRefs = useRef([]);
  const shadeRefs = useRef([]); // cast shadow, behind the body
  const glassRefs = useRef([]); // form shading and glass, over the surface
  const backLayerRef = useRef(null); // worlds on the far side, behind the star
  const layerRef = useRef(null); // worlds on the near side, in front of it

  const worlds = hats.map((hat, index) => ({
    hat,
    a: A0 + index * STEP,
    radius: MIN_PLANET + Math.sqrt(hat.count / max) * (MAX_PLANET - MIN_PLANET),
    period: BASE_PERIOD,
    phase: index / hats.length,
    // The culture's own inclination, on a hinge no other orbit shares.
    inc: (hat.inclination ?? 0) * RAD,
    node: index * NODE_SPREAD * RAD,
  }));

  useLayoutEffect(() => {
    const nodes = bodyRefs.current.filter(Boolean);
    if (!nodes.length) return undefined;
    const aMax = A0 + (worlds.length - 1) * STEP;

    // One phase offset per world, carried across frames and integrated.
    const shift = new Array(worlds.length).fill(0);

    const place = (elapsed, dt) => {
      const now = worlds.map((world, index) => {
        const theta = 2 * Math.PI * (elapsed / world.period + world.phase) + shift[index];
        const point = orbitPoint(world.a, theta, world.inc, world.node);
        // A step along the orbit, for the screen tangent the nudge needs.
        const ahead = orbitPoint(world.a, theta + 0.01, world.inc, world.node);
        return {
          theta,
          ...point,
          sx: screenX(point),
          sy: screenY(point),
          tx: screenX(ahead) - screenX(point),
          ty: screenY(ahead) - screenY(point),
        };
      });
      const scaleAt = (z) => 1 + DEPTH * (z / aMax);

      // Advance the offsets: crowding pushes, the spring pulls back.
      if (dt > 0) {
        const force = new Array(worlds.length).fill(0);
        for (let i = 0; i < worlds.length; i += 1) {
          for (let j = i + 1; j < worlds.length; j += 1) {
            const dx = now[j].sx - now[i].sx;
            const dy = now[j].sy - now[i].sy;
            const distance = Math.hypot(dx, dy) || 1e-3;
            const need =
              (worlds[i].radius * scaleAt(now[i].z) + worlds[j].radius * scaleAt(now[j].z)) *
                SHELL +
              CLEAR;
            if (distance >= need) continue;

            // Unit vector from i toward j, and each orbit's tangent on screen.
            const ux = dx / distance;
            const uy = dy / distance;
            // Taken from the projected path, since the planes no longer match.
            const tix = now[i].tx;
            const tiy = now[i].ty;
            const tjx = now[j].tx;
            const tjy = now[j].ty;
            const li = Math.hypot(tix, tiy) || 1;
            const lj = Math.hypot(tjx, tjy) || 1;

            // How much of each tangent actually points at the other world.
            const leverI = (tix * ux + tiy * uy) / li;
            const leverJ = (tjx * ux + tjy * uy) / lj;
            // Radially stacked: no tangent points anywhere useful, so break the
            // tie by hand rather than letting the pair sit locked together.
            const stalled = 1 - Math.min(Math.max(Math.abs(leverI), Math.abs(leverJ)) / LEVER, 1);

            const crowding = (need - distance) / need;
            force[i] += crowding * (stalled - leverI);
            force[j] += crowding * (leverJ - stalled);
          }
        }
        for (let i = 0; i < worlds.length; i += 1) {
          shift[i] = clamp(
            shift[i] + (PUSH * force[i] - SETTLE * shift[i]) * dt,
            -MAX_NUDGE,
            MAX_NUDGE,
          );
        }
      }

      const order = [];
      worlds.forEach((world, index) => {
        const node = nodes[index];
        if (!node) return;
        const { z, sx, sy } = now[index];
        const scale = scaleAt(z);
        node.setAttribute(
          'transform',
          `translate(${sx.toFixed(2)} ${sy.toFixed(2)}) scale(${scale.toFixed(3)})`,
        );

        /*
         * Turn each world's lighting to face the star.
         *
         * The shared sphere recipe lights from directly overhead, which is the
         * page's elevation model and is why these read flat out here: nine
         * bodies all lit identically look like nine discs. A solar system has
         * its own light source sitting right in the middle of it, so the crown,
         * the terminator and the cast shadow all swing round as a world comes
         * about. That swing is the depth cue -- far more of one than scale is.
         *
         * The gradients put the highlight at the top, so the rotation is the
         * angle to the star plus a quarter turn. Only the shading turns; the
         * surface texture and the mark stay put.
         */
        const toStar = Math.atan2(CY - sy, CX - sx) * (180 / Math.PI);
        const turn = `rotate(${(toStar + 90).toFixed(1)})`;
        const shade = shadeRefs.current[index];
        if (shade) shade.setAttribute('transform', turn);
        const glass = glassRefs.current[index];
        if (glass) glass.setAttribute('transform', turn);

        // Distant worlds sit back in the haze.
        node.style.opacity = (0.72 + 0.28 * ((z / aMax + 1) / 2)).toFixed(3);
        order.push({ node, z });
      });

      /*
       * Back to front, so a far world is occluded by nearer ones -- and split
       * across the star, so the far side genuinely passes BEHIND Luca instead
       * of being painted over him. Both layers keep their depth order, so a
       * world crossing the centre line just recedes; nothing pops.
       */
      order.sort((p, q) => p.z - q.z);
      const back = backLayerRef.current;
      const front = layerRef.current;
      order.forEach(({ node, z }) => {
        const layer = z < 0 ? back : front;
        if (layer) layer.appendChild(node);
      });
    };

    if (reduced) {
      // No animation to carry the offsets, so settle them here: two seconds of
      // simulated time, then the frame that gets drawn.
      for (let step = 0; step < 120; step += 1) place(0, 1 / 60);
      place(0, 0);
      return undefined;
    }

    let frame;
    const started = performance.now();
    let last = started;
    const tick = (now) => {
      const dt = Math.min((now - last) / 1000, MAX_STEP);
      last = now;
      place((now - started) / 1000, dt);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // worlds is derived from hats; re-running on hats change is correct.
  }, [hats, reduced, max]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`hat-orrery${activeHat ? ' is-filtered' : ''}${interactive ? '' : ' is-static'}`}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="How often each hat was worn"
        onClick={interactive ? onClear : undefined}
      >
        <defs>
          <SphereDefs uid={uid} />
          <SurfaceDefs uid={uid} />
          {worlds.map(({ hat, radius }) => (
            <clipPath key={hat.id} id={`orb-clip-${hat.id}-${uid}`}>
              <circle cx={0} cy={0} r={radius} />
            </clipPath>
          ))}
          {/*
            * Form shading, stronger than the shared sphere recipe. That one is
            * tuned for cards and buttons sitting flat on a page; a planet needs
            * a real terminator and a dark limb to read as a ball.
            */}
          <radialGradient id={`orb-form-${uid}`} cx="44%" cy="24%" r="80%">
            <stop offset="0%" stopColor="#fff" stopOpacity=".34" />
            <stop offset="30%" stopColor="#fff" stopOpacity=".05" />
            <stop offset="62%" stopColor="#000" stopOpacity=".12" />
            <stop offset="86%" stopColor="#000" stopOpacity=".42" />
            <stop offset="100%" stopColor="#000" stopOpacity=".66" />
          </radialGradient>

          <clipPath id={`star-clip-${uid}`}>
            <circle cx={CX} cy={CY} r={STAR_R} />
          </clipPath>

          <radialGradient id={`star-${uid}`} cx="50%" cy="32%" r="70%">
            <stop offset="0%" stopColor="var(--tmc-action-primary-hover)" />
            <stop offset="100%" stopColor="var(--tmc-action-primary)" />
          </radialGradient>
          <radialGradient id={`corona-${uid}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--tmc-action-primary)" stopOpacity=".22" />
            <stop offset="100%" stopColor="var(--tmc-action-primary)" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Gives empty space something to hit; the handler lives on the root
            so the star and the orbit strokes clear the filter too. */}
        <rect
          className="orrery-backdrop"
          x={0}
          y={0}
          width={W}
          height={H}
          fill="transparent"
        />

        {/* Far half of every orbit, behind the star. */}
        {worlds.map(({ hat, a, inc, node }) => (
          <path
            key={`far-${hat.id}`}
            className={`orrery-track is-far${activeHat === hat.id ? ' is-active' : ''}`}
            d={halfOrbit(a, false, inc, node)}
            style={{ '--track': `var(--tmc-hat-${hat.id})` }}
          />
        ))}

        {/* Worlds on the far side of the disc, which the star occludes. */}
        <g ref={backLayerRef} />

        {/*
          * The star is Luca.
          *
          * The backing disc sits under the photograph so the soft alpha at its
          * edge lands on something rather than on the page, and the glass pass
          * goes over the top so he takes the same limb and rim as every world
          * around him -- a body in the system, not a portrait pasted onto it.
          */}
        <circle cx={CX} cy={CY} r={STAR_R * 2.2} fill={`url(#corona-${uid})`} pointerEvents="none" />
        <circle className="orrery-star" cx={CX} cy={CY} r={STAR_R} fill={`url(#star-${uid})`} />
        <image
          className="orrery-star-face"
          href={`${process.env.PUBLIC_URL}/luca.png`}
          x={CX - STAR_R}
          y={CY - STAR_R}
          width={STAR_R * 2}
          height={STAR_R * 2}
          clipPath={`url(#star-clip-${uid})`}
          preserveAspectRatio="xMidYMid slice"
        />
        <SphereGlass uid={uid} cx={CX} cy={CY} r={STAR_R} />

        {/* Near half of every orbit, in front of the star. */}
        {worlds.map(({ hat, a, inc, node }) => (
          <path
            key={`near-${hat.id}`}
            className={`orrery-track${activeHat === hat.id ? ' is-active' : ''}`}
            d={halfOrbit(a, true, inc, node)}
            style={{ '--track': `var(--tmc-hat-${hat.id})` }}
          />
        ))}

        {/* Worlds, re-ordered by depth every frame. */}
        <g ref={layerRef}>
          {worlds.map(({ hat, radius }, index) => {
            const dimmed = activeHat && activeHat !== hat.id;
            return (
              <g
                key={hat.id}
                ref={(node) => {
                  bodyRefs.current[index] = node;
                }}
                className={`orrery-system${dimmed ? ' is-dimmed' : ''}${
                  activeHat === hat.id ? ' is-active' : ''
                }`}
              >
                {/* Turns with the light, so the contact shadow falls away
                    from the star rather than always straight down. */}
                <g
                  ref={(node) => {
                    shadeRefs.current[index] = node;
                  }}
                >
                  <SphereAtmosphere
                    uid={uid}
                    cx={0}
                    cy={0}
                    r={radius}
                    color={`var(--tmc-body-${hat.id})`}
                    dimmed={dimmed}
                  />
                </g>
                <g
                  className="orrery-body"
                  {...(interactive
                    ? {
                        role: 'button',
                        tabIndex: 0,
                        'aria-pressed': activeHat === hat.id,
                        'aria-label': `${hat.name}: ${hat.count} pieces of work`,
                        onClick: (event) => {
                          event.stopPropagation();
                          onToggle(hat.id);
                        },
                        onKeyDown: (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onToggle(hat.id);
                          }
                        },
                      }
                    : {})}
                >
                  <g clipPath={`url(#orb-clip-${hat.id}-${uid})`}>
                    <PlanetSurface
                      uid={uid}
                      cx={0}
                      cy={0}
                      r={radius}
                      color={`var(--tmc-body-${hat.id})`}
                      surface={HAT_SURFACE[hat.id]}
                      tilt={HAT_TILT[hat.id] ?? 0}
                    />
                  </g>
                  {/*
                    * The mark goes on BEFORE the light, not after.
                    *
                    * Painted last it sits over the terminator and the specular,
                    * and a flat white glyph laid across a lit sphere reads as a
                    * sticker -- which flattens the whole body, no matter how
                    * good the shading underneath is. Underneath, the crown
                    * brightens it and the limb shades it, so it belongs to the
                    * surface the way a marking on a real world would.
                    *
                    * It stays outside the rotating group: the light goes round,
                    * the mark stays upright and readable.
                    */}
                  <HatGlyph hatId={hat.id} size={radius * GLYPH} halo />
                  <g
                    className="orrery-shade"
                    ref={(node) => {
                      glassRefs.current[index] = node;
                    }}
                  >
                    <circle cx={0} cy={0} r={radius} fill={`url(#orb-form-${uid})`} />
                    <SphereGlass uid={uid} cx={0} cy={0} r={radius} />
                    {/*
                      * The shell the career mark shows so clearly at size: the
                      * textured core sits inside a slightly larger glass sphere,
                      * and the gap catches light as a bright crescent.
                      */}
                    <circle
                      className="orrery-shell"
                      cx={0}
                      cy={0}
                      r={radius * SHELL}
                      fill="none"
                      stroke={`url(#sph-rim-${uid})`}
                      strokeWidth={Math.max(0.7, radius * 0.085)}
                    />
                  </g>
                  <text className="orrery-label" x={0} y={-(radius + 13)} textAnchor="middle">
                    {`${hat.name} · ${hat.count}`}
                  </text>
                </g>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};

export default HatOrrery;
